/**
 * Keep in sync across the 3 apps:
 * frontend-lab/src/utils/webVideoCompatibility.js
 * frontend/src/utils/webVideoCompatibility.js
 * frontend-grader/src/utils/webVideoCompatibility.js
 *
 * Web-safe video: H.264 (avc1/avc3) 8-bit 4:2:0 in MP4/MOV.
 * Lab media is authored up to 1080p; 4K is not a requirement.
 */

export const MAX_WEB_VIDEO_MBPS = 50
export const MAX_WEB_VIDEO_BYTES = 200 * 1024 * 1024
export const MAX_MOOV_BYTES = 50 * 1024 * 1024
export const HIDDEN_VIDEO_PROBE_MS = 10_000

export const WEB_VIDEO_ACCEPT = 'video/mp4,video/quicktime,.mp4,.mov'
export const WEB_VIDEO_DROPZONE_HINT =
  'MP4 or MOV (H.264), up to 200 MB per file. Split longer footage into multiple videos.'

const WEB_SAFE_VIDEO = new Set(['avc1', 'avc3'])
const PRORES_VIDEO = new Set(['apch', 'apcn', 'apcs', 'apco', 'ap4h', 'ap4x'])
const HEVC_VIDEO = new Set(['hvc1', 'hev1', 'dvh1', 'dvhe'])
/** High 10, High 4:2:2, High 4:4:4 — not decodable in typical browsers. */
const BLOCKED_AVC_PROFILES = new Set([110, 122, 244])

const MSG = {
  prores:
    'This file is Apple ProRes, which browsers cannot play. Export H.264 (avc1) + AAC in an MP4 with faststart and try again.',
  hevc: 'This file is HEVC (H.265). Chrome on Windows cannot display it. Re-export as H.264 MP4 (iPhone: Camera → Formats → Most Compatible).',
  unsupported_profile:
    'This H.264 file uses a profile browsers cannot play (10-bit or 4:2:2/4:4:4). Please export as H.264 High or Main, 8-bit 4:2:0, in an MP4.',
  unsupported_codec:
    'This video codec cannot be played in browsers. Please export it as H.264 (avc1) MP4 at up to 1080p.',
  unreadable:
    "This video's image track cannot be decoded in this browser. Please export it as H.264 MP4 (avc1, 8-bit 4:2:0) at up to 1080p.",
}

function bitrateMessage(mbps) {
  return `This video is too heavy for web playback (${formatMbps(mbps)} Mbps). Please export it as H.264 MP4 at up to 1080p.`
}

function formatMbps(n) {
  const rounded = Math.round(Number(n) * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

/** File size for alerts: always one decimal, e.g. "243.7 MB". */
export function formatVideoSizeMb(bytes) {
  const mb = Number(bytes) / (1024 * 1024)
  if (!Number.isFinite(mb) || mb < 0) return '0.0 MB'
  return `${mb.toFixed(1)} MB`
}

/** Limit label: no decimal, e.g. "200 MB". */
export function formatVideoLimitMb(bytes = MAX_WEB_VIDEO_BYTES) {
  const mb = Number(bytes) / (1024 * 1024)
  if (!Number.isFinite(mb) || mb < 0) return '0 MB'
  return `${Math.round(mb)} MB`
}

function fileTooLargeMessage(sizeBytes, maxBytes = MAX_WEB_VIDEO_BYTES) {
  return `This video is ${formatVideoSizeMb(sizeBytes)} (limit ${formatVideoLimitMb(maxBytes)} per file). Split it into shorter clips and upload them as separate videos.`
}

function fourcc(bytes, offset) {
  if (!bytes || offset + 4 > bytes.length) return ''
  return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3])
}

function u32(bytes, offset) {
  if (!bytes || offset + 4 > bytes.length) return 0
  return (
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  )
}

function u64(bytes, offset) {
  const hi = u32(bytes, offset)
  const lo = u32(bytes, offset + 4)
  return hi * 0x100000000 + lo
}

async function readBytes(file, offset, length) {
  if (!file || length <= 0 || offset >= file.size) return new Uint8Array(0)
  const start = Math.max(0, offset)
  const end = Math.min(file.size, start + length)
  if (end <= start) return new Uint8Array(0)
  const buf = await file.slice(start, end).arrayBuffer()
  return new Uint8Array(buf)
}

/**
 * @returns {{ size: number, type: string, headerSize: number } | null}
 */
function parseBoxHeader(bytes, offset = 0) {
  if (bytes.length < offset + 8) return null
  let size = u32(bytes, offset)
  const type = fourcc(bytes, offset + 4)
  let headerSize = 8
  if (size === 1) {
    if (bytes.length < offset + 16) return null
    size = u64(bytes, offset + 8)
    headerSize = 16
  }
  if (!Number.isFinite(size) || size < 0) return null
  return { size, type, headerSize }
}

async function readTopLevelHeader(file, offset) {
  const head = await readBytes(file, offset, 16)
  if (head.length < 8) return null
  const parsed = parseBoxHeader(head, 0)
  if (!parsed) return null
  if (parsed.size === 0) parsed.size = file.size - offset
  if (parsed.size < parsed.headerSize) return null
  if (offset + parsed.size > file.size) parsed.size = file.size - offset
  return parsed
}

function walkBoxes(buf, start, end, onBox) {
  let offset = start
  while (offset + 8 <= end) {
    const header = parseBoxHeader(buf, offset)
    if (!header) break
    let { size, type, headerSize } = header
    if (size === 0) size = end - offset
    if (size < headerSize || offset + size > end) break
    onBox({
      type,
      offset,
      size,
      headerSize,
      payloadStart: offset + headerSize,
      payloadEnd: offset + size,
    })
    if (size === 0) break
    offset += size
  }
}

function parseMvhd(buf, payloadStart, payloadEnd) {
  if (payloadEnd - payloadStart < 8) return { durationSeconds: null }
  const version = buf[payloadStart]
  let timescale
  let duration
  if (version === 1) {
    // FullBox(4) + creation(8) + modification(8) + timescale(4) + duration(8)
    if (payloadEnd - payloadStart < 32) return { durationSeconds: null }
    timescale = u32(buf, payloadStart + 20)
    duration = u64(buf, payloadStart + 24)
  } else {
    // FullBox(4) + creation(4) + modification(4) + timescale(4) + duration(4)
    if (payloadEnd - payloadStart < 20) return { durationSeconds: null }
    timescale = u32(buf, payloadStart + 12)
    duration = u32(buf, payloadStart + 16)
  }
  if (!timescale || !Number.isFinite(duration) || duration <= 0) {
    return { durationSeconds: null, timescale, duration }
  }
  const durationSeconds = duration / timescale
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return { durationSeconds: null, timescale, duration }
  }
  return { durationSeconds, timescale, duration }
}

function parseHdlrType(buf, payloadStart, payloadEnd) {
  // FullBox (4) + pre_defined (4) + handler_type (4)
  if (payloadEnd - payloadStart < 12) return ''
  return fourcc(buf, payloadStart + 8)
}

function parseAvcCProfile(buf, entryOffset, entrySize) {
  const payloadStart = entryOffset + 8
  // 8 SampleEntry + 70 VisualSampleEntry fields, then child boxes (avcC, pasp, …)
  const childrenStart = payloadStart + 78
  const entryEnd = entryOffset + entrySize
  if (childrenStart + 8 > entryEnd) return { unreadable: true }

  let profile = null
  let found = false
  walkBoxes(buf, childrenStart, entryEnd, (box) => {
    if (box.type !== 'avcC' || found) return
    found = true
    if (box.payloadStart + 2 > box.payloadEnd) return
    profile = buf[box.payloadStart + 1]
  })

  if (!found || profile == null || !Number.isFinite(profile)) return { unreadable: true }
  return { profile }
}

function parseStsd(buf, payloadStart, payloadEnd) {
  // FullBox (4) + entry_count (4)
  if (payloadEnd - payloadStart < 8) return { unreadable: true }
  const entryCount = u32(buf, payloadStart + 4)
  let offset = payloadStart + 8
  let codec = null
  let profile = null
  let avcCUnreadable = false

  for (let i = 0; i < entryCount && offset + 8 <= payloadEnd; i++) {
    const header = parseBoxHeader(buf, offset)
    if (!header) break
    let { size, type } = header
    if (size === 0) size = payloadEnd - offset
    if (size < header.headerSize || offset + size > payloadEnd) break
    if (!codec) codec = type
    if (WEB_SAFE_VIDEO.has(type)) {
      const avcC = parseAvcCProfile(buf, offset, size)
      if (avcC.unreadable) avcCUnreadable = true
      else profile = avcC.profile
    }
    offset += size
  }

  return { codec, profile, avcCUnreadable }
}

function parseMdia(buf, mdiaPayloadStart, mdiaPayloadEnd) {
  let handler = ''
  let stsd = null
  walkBoxes(buf, mdiaPayloadStart, mdiaPayloadEnd, (mdiaBox) => {
    if (mdiaBox.type === 'hdlr') {
      handler = parseHdlrType(buf, mdiaBox.payloadStart, mdiaBox.payloadEnd)
    }
  })
  walkBoxes(buf, mdiaPayloadStart, mdiaPayloadEnd, (mdiaBox) => {
    if (mdiaBox.type !== 'minf') return
    walkBoxes(buf, mdiaBox.payloadStart, mdiaBox.payloadEnd, (minfBox) => {
      if (minfBox.type !== 'stbl') return
      walkBoxes(buf, minfBox.payloadStart, minfBox.payloadEnd, (stblBox) => {
        if (stblBox.type !== 'stsd') return
        stsd = parseStsd(buf, stblBox.payloadStart, stblBox.payloadEnd)
      })
    })
  })
  return { handler, stsd }
}

function parseMoov(buf) {
  const header = parseBoxHeader(buf, 0)
  if (!header || header.type !== 'moov') return { unreadable: true }
  const moovStart = header.headerSize
  const moovEnd = header.size === 0 ? buf.length : Math.min(buf.length, header.size)

  let durationSeconds = null
  let videoCodec = null
  let audioCodec = null
  let profile = null
  let avcCUnreadable = false
  let sawVide = false

  walkBoxes(buf, moovStart, moovEnd, (box) => {
    if (box.type === 'mvhd' && durationSeconds == null) {
      durationSeconds = parseMvhd(buf, box.payloadStart, box.payloadEnd).durationSeconds
      return
    }
    if (box.type !== 'trak') return

    let handler = ''
    let stsd = null
    walkBoxes(buf, box.payloadStart, box.payloadEnd, (trakBox) => {
      if (trakBox.type !== 'mdia') return
      const mdia = parseMdia(buf, trakBox.payloadStart, trakBox.payloadEnd)
      handler = mdia.handler
      stsd = mdia.stsd
    })

    if (handler === 'vide' && !sawVide) {
      sawVide = true
      videoCodec = stsd?.codec || null
      profile = stsd?.profile ?? null
      if (stsd?.avcCUnreadable) avcCUnreadable = true
    } else if (handler === 'soun' && !audioCodec) {
      audioCodec = stsd?.codec || null
    }
  })

  if (avcCUnreadable && WEB_SAFE_VIDEO.has(videoCodec)) return { unreadable: true }
  return { durationSeconds, videoCodec, audioCodec, profile }
}

async function parseIsoBmff(file) {
  let offset = 0
  let hops = 0
  const maxHops = 64

  while (offset < file.size && hops < maxHops) {
    hops += 1
    const header = await readTopLevelHeader(file, offset)
    if (!header) return { unreadable: true }
    if (header.type === 'moov') {
      if (header.size > MAX_MOOV_BYTES) return { unreadable: true }
      const moov = await readBytes(file, offset, header.size)
      if (moov.length < 16) return { unreadable: true }
      return parseMoov(moov)
    }
    const next = offset + header.size
    if (next <= offset) return { unreadable: true }
    offset = next
  }

  return { unreadable: true }
}

function reject(reason, extra = {}) {
  let message = MSG[reason] || MSG.unreadable
  if (reason === 'bitrate_too_high') message = bitrateMessage(extra.mbps)
  if (reason === 'file_too_large') {
    message = fileTooLargeMessage(extra.sizeBytes, extra.maxBytes)
  }
  return { ...extra, ok: false, reason, message }
}

function accept(extra = {}) {
  return { ok: true, ...extra }
}

async function sniffIsIsoBmff(file) {
  const head = await readBytes(file, 0, 12)
  return head.length >= 8 && fourcc(head, 4) === 'ftyp'
}

function looksLikeIsoContainer(file) {
  const name = String(file?.name || '').toLowerCase()
  const type = String(file?.type || '').toLowerCase()
  if (
    type === 'video/webm' ||
    type === 'video/ogg' ||
    name.endsWith('.webm') ||
    name.endsWith('.ogv') ||
    name.endsWith('.ogg')
  ) {
    return false
  }
  if (type === 'video/mp4' || type === 'video/quicktime' || /\.(mp4|mov|m4v|qt)$/.test(name)) {
    return true
  }
  return null
}

function probeWithHiddenVideo(file) {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !file) {
    return Promise.resolve(reject('unreadable'))
  }

  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', '')

    let settled = false
    const finish = (result) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      video.onloadedmetadata = null
      video.onerror = null
      video.removeAttribute('src')
      video.load()
      URL.revokeObjectURL(objectUrl)
      resolve(result)
    }

    const timer = window.setTimeout(() => finish(reject('unreadable')), HIDDEN_VIDEO_PROBE_MS)

    video.onloadedmetadata = () => {
      if (video.videoWidth > 0) {
        finish(
          accept({
            videoCodec: null,
            audioCodec: null,
            durationSeconds: Number.isFinite(video.duration) ? video.duration : null,
          })
        )
        return
      }
      finish(reject('unreadable'))
    }
    video.onerror = () => finish(reject('unreadable'))
    video.src = objectUrl
  })
}

function decideFromMoov(file, parsed) {
  const videoCodec = parsed.videoCodec || null
  const audioCodec = parsed.audioCodec || null
  const profile = parsed.profile ?? null
  const durationSeconds = parsed.durationSeconds ?? null
  const base = { videoCodec, audioCodec, profile, durationSeconds }

  if (!videoCodec) return { fallback: true }

  if (PRORES_VIDEO.has(videoCodec)) return reject('prores', base)
  if (HEVC_VIDEO.has(videoCodec)) return reject('hevc', base)

  if (WEB_SAFE_VIDEO.has(videoCodec)) {
    if (BLOCKED_AVC_PROFILES.has(profile)) return reject('unsupported_profile', base)

    const durationOk =
      Number.isFinite(durationSeconds) && durationSeconds > 0 && durationSeconds !== Infinity
    if (durationOk && Number.isFinite(file.size)) {
      const mbps = (file.size * 8) / durationSeconds / 1e6
      if (Number.isFinite(mbps) && mbps > MAX_WEB_VIDEO_MBPS) {
        return reject('bitrate_too_high', { ...base, mbps })
      }
      return accept({ ...base, mbps })
    }
    return accept(base)
  }

  return reject('unsupported_codec', base)
}

/** Codec/profile → bitrate, then size. Size never overrides a codec/bitrate reject. */
function applySizeLimit(file, result) {
  if (!result?.ok) return result
  const sizeBytes = typeof file?.size === 'number' ? file.size : null
  if (sizeBytes != null && sizeBytes > MAX_WEB_VIDEO_BYTES) {
    return reject('file_too_large', {
      ...result,
      sizeBytes,
      maxBytes: MAX_WEB_VIDEO_BYTES,
    })
  }
  return sizeBytes != null ? { ...result, sizeBytes, maxBytes: MAX_WEB_VIDEO_BYTES } : result
}

/**
 * Inspect a File before upload. Never throws.
 * Validation order: codec/profile → bitrate → size.
 * @param {File} file
 */
export async function inspectWebVideoFile(file) {
  if (!file || typeof file.size !== 'number') return reject('unreadable')

  const isoGuess = looksLikeIsoContainer(file)
  const useIso = isoGuess === true || (isoGuess === null && (await sniffIsIsoBmff(file)))

  if (useIso) {
    try {
      const parsed = await parseIsoBmff(file)
      if (!parsed?.unreadable) {
        const decision = decideFromMoov(file, parsed)
        if (!decision.fallback) return applySizeLimit(file, decision)
      }
    } catch {
      // fall through to hidden <video> probe
    }
    return applySizeLimit(file, await probeWithHiddenVideo(file))
  }

  return applySizeLimit(file, await probeWithHiddenVideo(file))
}

/** Inspect every file before any upload. Rejects do not short-circuit the rest of the list. */
export async function inspectWebVideoFiles(files) {
  const list = Array.from(files || []).filter(Boolean)
  const results = []
  for (const file of list) {
    const inspected = await inspectWebVideoFile(file)
    results.push({ file, fileName: file?.name || 'Video', ...inspected })
  }
  return results
}

export function webVideoRejectMessage(result) {
  if (!result || result.ok) return ''
  return result.message || MSG.unreadable
}

export function webVideoAlertTitle(rejects) {
  const list = Array.isArray(rejects) ? rejects.filter((r) => r && r.ok === false) : []
  if (list.length > 1) return "These videos can't be uploaded"
  if (list.length === 1) return "This video can't be uploaded"
  return "This video can't be uploaded"
}

export function webVideoAlertBody(rejects) {
  const list = Array.isArray(rejects) ? rejects.filter((r) => r && r.ok === false) : []
  if (list.length === 0) return ''
  if (list.length === 1) return webVideoRejectMessage(list[0])
  return list
    .map((r) => {
      const name = r.fileName || r.file?.name || 'Video'
      return `${name}: ${webVideoRejectMessage(r)}`
    })
    .join('\n')
}
